'use client'

import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const inputVariants = cva(
  'flex w-full rounded-md border text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 transition-colors',
  {
    variants: {
      variant: {
        default: 'border-input bg-background',
        filled: 'border-input bg-surface-hover',
      },
      inputSize: {
        default: 'h-10 px-3 py-2',
        sm: 'h-8 px-2 py-1 text-xs',
        lg: 'h-12 px-4 py-3',
      },
    },
    defaultVariants: {
      variant: 'default',
      inputSize: 'default',
    },
  }
)

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  error?: boolean
  leftAddon?: React.ReactNode
  rightAddon?: React.ReactNode
  label?: string
  helperText?: string
  errorMessage?: string
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, variant, inputSize, error, leftAddon, rightAddon, label, helperText, errorMessage, ...props }, ref) => {
    const inputElement = (
      <div className={cn('relative flex items-center', error && 'ring-1 ring-[var(--color-pink)] rounded-md')}>
        {leftAddon && (
          <div className="absolute left-3 flex items-center text-muted-foreground">
            {leftAddon}
          </div>
        )}
        <input
          className={cn(
            inputVariants({ variant, inputSize }),
            leftAddon && 'pl-10',
            rightAddon && 'pr-10',
            className
          )}
          ref={ref}
          {...props}
        />
        {rightAddon && (
          <div className="absolute right-3 flex items-center text-muted-foreground">
            {rightAddon}
          </div>
        )}
      </div>
    )

    if (!label && !helperText && !errorMessage) {
      return inputElement
    }

    return (
      <div className="space-y-1.5">
        {label && (
          <label className="text-sm font-medium text-foreground">
            {label}
          </label>
        )}
        {inputElement}
        {error && errorMessage ? (
          <p className="text-xs text-[var(--color-pink)]">{errorMessage}</p>
        ) : helperText ? (
          <p className="text-xs text-muted-foreground">{helperText}</p>
        ) : null}
      </div>
    )
  }
)
Input.displayName = 'Input'

export { Input, inputVariants }
